import { useLocalizedList } from "./useLocalizedContent";
import type { BlogPost } from "@/lib/blog";

/** Translate editorial prose only; retain IDs, attribution, dates and access. */
export function useLocalizedBlogPost(post: BlogPost | undefined): BlogPost | undefined {
  const items = post ? [
    ...["title", "description", "intro"].map((field) => ({ field, sourceText: post[field as "title" | "description" | "intro"] })),
    ...post.tags.map((sourceText, i) => ({ field: `tag${i}`, sourceText })),
    ...post.sections.flatMap((s, i) => [
      ...(s.heading ? [{ field: `h${i}`, sourceText: s.heading }] : []),
      ...s.paragraphs.map((sourceText, j) => ({ field: `p${i}:${j}`, sourceText })),
    ]),
    ...(post.faqs ?? []).flatMap((f, i) => [{ field: `q${i}`, sourceText: f.question }, { field: `a${i}`, sourceText: f.answer }]),
  ].map((item) => ({ ...item, entityType: "blog", entityId: post.slug })) : [];
  const translated = useLocalizedList(items);
  if (!post) return undefined;
  const text = (field: string, original: string) => translated.map.get(`${post.slug}::${field}`) ?? original;
  return {
    ...post,
    title: text("title", post.title), description: text("description", post.description), intro: text("intro", post.intro),
    tags: post.tags.map((tag, i) => text(`tag${i}`, tag)),
    sections: post.sections.map((s, i) => ({ ...s, ...(s.heading ? { heading: text(`h${i}`, s.heading) } : {}),
      paragraphs: s.paragraphs.map((p, j) => text(`p${i}:${j}`, p)),
    })),
    ...(post.faqs ? { faqs: post.faqs.map((f, i) => ({ question: text(`q${i}`, f.question), answer: text(`a${i}`, f.answer) })) } : {}),
  };
}